import { Container } from "reactstrap";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get, post } from "../../../../utils/helpers/api";
import { useNavigate } from "react-router-dom";
import IconButton from "../../../../components/Buttons/IconButton";
import { IoCreateOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import { AgGridReact } from "ag-grid-react";
import DefaultPagination from "../../../../components/Pagination";
import CreateCategoryModal from "./Modals/create";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { useTranslation } from "react-i18next";
import AgGridTable from "../../../../components/Tables/AgGrid";

function ActionsCellRenderer({ data, context }) {
  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCategoryToEdit(data);
          context.setAddCategoryModalOpen(true);
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setCategoryToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function CategoriesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addCategoryModalOpen, setAddCategoryModalOpen] = useState(false);
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const getCategories = useCallback(async () => {
    setLoading(true);
    const data = await get(`/inventory/categories?page=${currentPage}&page_size=${pageSize}`);
    if(data?.success) {
      setRowData(data.categories.data);
      setTotalPages(data.categories.last_page);
      setTotalRecords(data.categories.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  const deleteCategory = async () => {
    setLoading(true);
    const data = await del(`/inventory/categories/delete/${categoryToDelete}`);
    if(data?.success) {
      getCategories();
      setCategoryToDelete(null);
    }
    setLoading(false);
  }

  const columnDefs = useMemo(() => [
    { field: 'name', headerName: t('common.name'), flex: 1, sortable: true },
    { field: 'total_products', headerName: t('inventory.totalProducts'), flex: 1, sortable: true },
    {
      field: 'is_active',
      headerName: t('common.active'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => params.value ? t('common.yes') : t('common.no')
    },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(() => ({ navigate, setCategoryToEdit, setAddCategoryModalOpen, setCategoryToDelete }), [navigate]);

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('inventory.categories')} onReload={getCategories} dataTotal={totalRecords}>
          <DefaultButton color="primary" size="sm" onClick={() => {setAddCategoryModalOpen(true); setCategoryToEdit(null);}}>
            {t('inventory.newCategory')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable 
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <CreateCategoryModal 
        isOpen={addCategoryModalOpen} 
        toggle={() => setAddCategoryModalOpen(false)} 
        refreshCategories={getCategories} 
        editData={categoryToEdit}
      />

      <ConfirmationModal
        isOpen={categoryToDelete !== null}
        title={t('inventory.deleteCategory')}
        message={t('inventory.deleteCategoryConfirm')}
        onConfirm={deleteCategory}
        toggle={() => setCategoryToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default CategoriesPage;